import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import RevenueClinicInvoicesModal from "../../../../modals/RevenueClinicInvoicesModal";
import { format } from "date-fns";
import { fetchRevenueReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

// Currency formatting utility
const formatCurrency = (amount) => {
  if (amount == null) return "$0.00";
  return `$${Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

const RevenueReport = () => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState(format(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [activeView, setActiveView] = useState("summary");
  const [clinicModalOpen, setClinicModalOpen] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);

  const {
    data: revenueData,
    loading,
    error,
  } = useSelector((state) => state.reports.revenue || { data: null, loading: false, error: null });

  const data = revenueData?.data || {};
  const summary = data.summary || {};
  const byClinician = data.byClinician || [];
  const byClinic = data.byClinic || [];
  const byService = data.byService || [];
  const byStatus = data.byStatus || [];
  const dailyRevenue = data.dailyRevenue || [];
  const monthlyRevenue = data.monthlyRevenue || [];

  // Load initial data on component mount with default date range
  useEffect(() => {
    const initialStartDate = format(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), "yyyy-MM-dd");
    const initialEndDate = format(new Date(), "yyyy-MM-dd");
    dispatch(fetchRevenueReport({ startDate: initialStartDate, endDate: initialEndDate }));
  }, [dispatch]); // Only run on mount with static initial values

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(fetchRevenueReport({ startDate, endDate }));
  };

  const handleDownload = (viewType) => {
    let data = [];
    let fileName = "";
    let sheetName = "";

    switch (viewType) {
      case "clinician":
        data = byClinician.map((item) => ({
          "Clinician": item.clinicianName || "Unknown",
          "Total Revenue": Number(item.totalRevenue || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
          "Invoice Count": item.invoiceCount || 0,
          "Average Invoice": Number(item.avgInvoiceAmount || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
        }));
        fileName = `Revenue_by_Clinician_${startDate}_to_${endDate}.xlsx`;
        sheetName = "By Clinician";
        break;
      case "clinic":
        data = byClinic.map((item) => ({
          "Clinic": item.clinicName || "Unknown",
          "Total Revenue": Number(item.totalRevenue || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
          "Invoice Count": item.invoiceCount || 0,
          "Average Invoice": Number(item.avgInvoiceAmount || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
        }));
        fileName = `Revenue_by_Clinic_${startDate}_to_${endDate}.xlsx`;
        sheetName = "By Clinic";
        break;
      case "service":
        data = byService.map((item) => ({
          "Service Type": item._id || "Unknown",
          "Total Revenue": Number(item.totalRevenue || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
          "Invoice Count": item.invoiceCount || 0,
          "Average Amount": Number(item.avgServiceAmount || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
        }));
        fileName = `Revenue_by_Service_${startDate}_to_${endDate}.xlsx`;
        sheetName = "By Service";
        break;
      case "daily":
        data = dailyRevenue.map((item) => ({
          "Date": item._id,
          "Daily Revenue": Number(item.totalRevenue || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
          "Invoice Count": item.invoiceCount || 0,
        }));
        fileName = `Daily_Revenue_${startDate}_to_${endDate}.xlsx`;
        sheetName = "Daily Revenue";
        break;
      default:
        return;
    }

    if (data.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName);
  };

  const clinicianColumns = [
    { header: "Clinician", accessor: "clinicianName" },
    { header: "Total Revenue", accessor: (row) => formatCurrency(row.totalRevenue) },
    { header: "Invoice Count", accessor: "invoiceCount" },
    { header: "Avg Invoice", accessor: (row) => formatCurrency(row.avgInvoiceAmount) },
  ];

  const clinicColumns = [
    { header: "Clinic", accessor: "clinicName" },
    { header: "Total Revenue", accessor: (row) => formatCurrency(row.totalRevenue) },
    { header: "Invoice Count", accessor: "invoiceCount" },
    { header: "Avg Invoice", accessor: (row) => formatCurrency(row.avgInvoiceAmount) },
  ];

  const handleClinicRowClick = (clinicId) => {
    const row = byClinic.find((c) => String(c._id) === String(clinicId));
    if (!row) return;
    setSelectedClinic({ id: row._id, name: row.clinicName });
    setClinicModalOpen(true);
  };

  const serviceColumns = [
    { header: "Service Type", accessor: "_id" },
    { header: "Total Revenue", accessor: (row) => formatCurrency(row.totalRevenue) },
    { header: "Invoice Count", accessor: "invoiceCount" },
    { header: "Avg Amount", accessor: (row) => formatCurrency(row.avgServiceAmount) },
  ];

  const statusColumns = [
    { header: "Status", accessor: "_id" },
    { header: "Count", accessor: "count" },
    { header: "Amount", accessor: (row) => formatCurrency(row.totalRevenue) },
  ];

  const dailyColumns = [
    { header: "Date", accessor: "_id" },
    { header: "Daily Revenue", accessor: (row) => formatCurrency(row.totalRevenue) },
    { header: "Invoice Count", accessor: "invoiceCount" },
  ];

  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  return (
    <div className="space-y-6">
      {/* Date Range Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-sm shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Revenue Report</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </div>
      </form>

      {/* Show loading state */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading revenue data...</p>
          </div>
        </div>
      )}

      {/* Show data only if we have it and not loading */}
      {!loading && summary && Object.keys(summary).length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Invoiced</h4>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalInvoiced)}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Collected</h4>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.totalCollected)}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Outstanding</h4>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.totalOutstanding)}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Collection Rate</h4>
              <p className="text-2xl font-bold text-blue-600">{summary.collectionRate || "0"}%</p>
            </div>
          </div>

          {/* Additional Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Invoices</h4>
              <p className="text-xl font-semibold text-gray-900">{summary.totalInvoices || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Avg Invoice Amount</h4>
              <p className="text-xl font-semibold text-gray-900">${summary.avgInvoiceAmount?.toFixed(2) || "0.00"}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Min Invoice</h4>
              <p className="text-xl font-semibold text-gray-900">${summary.minInvoiceAmount?.toFixed(2) || "0.00"}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Max Invoice</h4>
              <p className="text-xl font-semibold text-gray-900">${summary.maxInvoiceAmount?.toFixed(2) || "0.00"}</p>
            </div>
          </div>

          {/* View Tabs */}
          <div className="bg-white shadow-sm rounded-sm">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {[
                  { key: "summary", label: "Summary" },
                  { key: "clinician", label: "By Clinician" },
                  { key: "clinic", label: "By Clinic" },
                  { key: "service", label: "By Service" },
                  { key: "status", label: "By Status" },
                  { key: "daily", label: "Daily Breakdown" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveView(tab.key)}
                    className={`${
                      activeView === tab.key
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {activeView === "summary" && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Revenue Summary</h3>
                  
                  {/* Monthly Trends */}
                  {monthlyRevenue.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-gray-700 mb-3">Monthly Revenue Trends</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {monthlyRevenue.map((month, index) => {
                          const [year, monthNum] = month._id.split('-');
                          const monthName = new Date(year, monthNum - 1).toLocaleDateString('en-US', { month: 'long' });
                          return (
                            <div key={index} className="bg-gray-50 p-4 rounded-sm">
                              <h5 className="text-sm font-medium text-gray-600">
                                {monthName} {year}
                              </h5>
                              <p className="text-lg font-semibold text-gray-900">
                                {formatCurrency(month.totalRevenue)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {month.invoiceCount} invoices
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Revenue by Status */}
                  {byStatus.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-gray-700 mb-3">Revenue by Invoice Status</h4>
                      <Table columns={statusColumns} data={byStatus} />
                    </div>
                  )}
                </div>
              )}

              {activeView === "clinician" && (
                <Table
                  columns={clinicianColumns}
                  data={byClinician}
                  title="Revenue by Clinician"
                  description="Revenue breakdown by individual clinicians"
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: () => handleDownload("clinician"),
                    },
                  ]}
                />
              )}

              {activeView === "clinic" && (
                <Table
                  columns={clinicColumns}
                  data={byClinic}
                  title="Revenue by Clinic"
                  description="Revenue breakdown by clinic location"
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: () => handleDownload("clinic"),
                    },
                  ]}
                  onRowClick={handleClinicRowClick}
                />
              )}

              {activeView === "service" && (
                <Table
                  columns={serviceColumns}
                  data={byService}
                  title="Revenue by Service Type"
                  description="Revenue breakdown by appointment/service type"
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: () => handleDownload("service"),
                    },
                  ]}
                />
              )}

              {activeView === "status" && (
                <Table
                  columns={statusColumns}
                  data={byStatus}
                  title="Revenue by Invoice Status"
                  description="Revenue breakdown by invoice payment status"
                />
              )}

              {activeView === "daily" && (
                <Table
                  columns={dailyColumns}
                  data={dailyRevenue}
                  title="Daily Revenue Breakdown"
                  description="Day-by-day revenue analysis"
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: () => handleDownload("daily"),
                    },
                  ]}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Show message if no data and not loading */}
      {!loading && (!summary || Object.keys(summary).length === 0) && (
        <div className="bg-white p-6 rounded-sm shadow-sm text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-600">
            No revenue data found for the selected date range. Try adjusting your date range or check if there are any invoices in the system for this period.
          </p>
        </div>
      )}
      {clinicModalOpen && selectedClinic && (
        <RevenueClinicInvoicesModal
          isOpen={clinicModalOpen}
          onClose={() => setClinicModalOpen(false)}
          clinic={selectedClinic}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </div>
  );
};

export default RevenueReport;